// #region IMPORTS
import { useEffect, useState } from 'react';
import { Object3D } from 'three';
import { GLTF } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { useSnapshot } from 'valtio';

import useUpdateCharacter from 'hooks/useUpdateCharacter';
import { animationGenerationStore, characterPickerStore } from 'stores';
import { downloadCharacter } from 'utils/downloadUtil';

// #endregion

export default function ActiveCharacter() {
  const [mesh, setMesh] = useState<Object3D | null>(null);
  const { activeCharacter } = useSnapshot(characterPickerStore.state);

  // Any change to character auto triggers any effects
  // attached to activeCharacter. Use the characters
  // unique ID as a trigger to remove unneeded rerenders
  const charID = activeCharacter?.PK ?? null;
  const readyOn = activeCharacter?.readyOn ?? null;

  // #region EFFECTS

  // Component onMount
  useEffect(() => {
    return () => {
      console.log('ActiveCharacter - Unmount');
    };
  }, []);

  // Start the character downloading
  useEffect(() => {
    if (charID != null) {
      // Snapshot creates readonly references which does not allow
      // for updating state. Find character in state to have a mutable
      // version that can be updated after downloading content.
      const char = characterPickerStore.state.characters?.find(
        (i) => i.PK === charID,
      );

      if (!char) {
        // Character not found in array
      } else if (!char.meshData.current) {
        // If Character mesh does not exist, download it
        animationGenerationStore.setLoading('mesh', true);
        downloadCharacter(char).then(() => {
          animationGenerationStore.setLoading('mesh', false);
        });
      }
    }
  }, [charID]);

  // Swop mesh on primitive, let fiber manage character in scene.
  // NOTE: Geometry is not being disposed when swopping characters. May need
  // to manage that in the future
  useEffect(() => {
    // This effect will trigger initially before activeCharacter has been set.
    // So make make sure to test if the data is available before doing anything
    if (activeCharacter?.meshData.current) {
      setMesh(
        (activeCharacter?.meshData.current.scene as unknown as Object3D) ||
          null,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readyOn]);

  // #endregion

  useUpdateCharacter(activeCharacter?.meshData.current as unknown as GLTF);

  return mesh ? <primitive object={mesh} /> : null;
}
